import { Link } from "react-router-dom";
import Logo from "@/components/Logo";

/**
 * Public /support — unauthenticated support landing page.
 *
 * Purpose: satisfy Apple App Store Connect's required HTTPS Support URL
 * field, and provide a stable public help entry point for contractors
 * and store reviewers. No backend, no contact form, no database — a
 * static, JSX-only page that reuses the same visual shell as /privacy,
 * /terms, and /data-security for brand consistency.
 *
 * Product Owner approved scope only:
 *   - JobClose AI Support heading
 *   - Support email reference (support@jobclose.ai)
 *   - Contact instructions (no invented SLA)
 *   - Simple help topics
 *   - Login / Privacy / Terms / Data Security cross-links
 *   - Account deletion / recovery guidance
 *   - English / Spanish support statement
 */
const Support = () => (
  <div className="min-h-screen bg-white" data-testid="support-page">
    <header className="border-b border-[var(--jc-border)]">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
        <Logo variant="full" theme="light" size="md" />
        <Link
          to="/"
          data-testid="support-back-home"
          className="text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
        >
          ← Back to site
        </Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
      <div className="overline mb-3">Help</div>
      <h1
        className="font-display text-4xl lg:text-5xl tracking-tight"
        data-testid="support-heading"
      >
        JobClose AI Support
      </h1>
      <div className="text-sm text-[var(--jc-text-soft)] mt-2">
        Contractor-built help for JobClose AI customers, teammates, and
        reviewers.
      </div>

      <div className="prose prose-slate max-w-none mt-10 text-[15px] leading-relaxed space-y-6">
        <section data-testid="support-contact">
          <h2 className="font-display text-xl mt-2 mb-2 text-[var(--jc-text)]">
            Contact support
          </h2>
          <p>
            Email us directly and we'll get back to you. Please include your
            account email, a brief description of what you were trying to do,
            and — if it helps — a screenshot.
          </p>
          <p>
            Support email:{" "}
            <a
              href="mailto:support@jobclose.ai"
              className="font-mono-jc underline"
              data-testid="support-email-link"
            >
              support@jobclose.ai
            </a>
          </p>
        </section>

        <section data-testid="support-topics">
          <h2 className="font-display text-xl mt-6 mb-2 text-[var(--jc-text)]">
            Common help topics
          </h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Signing in and password reset</li>
            <li>Starting your 7-day free trial</li>
            <li>Adding teammates to your JobClose AI organization</li>
            <li>
              Using Levi — your AI coworker — for voice, vision, and everyday
              contracting work
            </li>
            <li>Estimates, proposals, contracts, and change orders</li>
            <li>Scheduling and jobsite updates</li>
            <li>Invoices and getting paid</li>
            <li>Managing your subscription and billing</li>
          </ul>
        </section>

        <section data-testid="support-language">
          <h2 className="font-display text-xl mt-6 mb-2 text-[var(--jc-text)]">
            English and Spanish
          </h2>
          <p>
            JobClose AI supports both English and Spanish across the app and in
            customer-facing documents. You can email support in either language
            and we'll respond in the language you wrote to us in.
          </p>
          <p>
            JobClose AI ofrece soporte en inglés y español. Puedes escribirnos
            a{" "}
            <span className="font-mono-jc">support@jobclose.ai</span> en el
            idioma que prefieras y te responderemos en ese mismo idioma.
          </p>
        </section>

        <section data-testid="support-account-deletion">
          <h2 className="font-display text-xl mt-6 mb-2 text-[var(--jc-text)]">
            Delete or restore your account
          </h2>
          <p>
            You can permanently delete your JobClose AI account from{" "}
            <span className="whitespace-nowrap">Account → Danger Zone</span>{" "}
            inside the app. Deletion is a two-step process with a short
            recovery window so you can restore your account by signing back in
            during that window.
          </p>
          <p>
            If you're locked out and need help deleting or restoring your
            account, email{" "}
            <a
              href="mailto:support@jobclose.ai"
              className="font-mono-jc underline"
            >
              support@jobclose.ai
            </a>{" "}
            from the address on file and we'll help.
          </p>
        </section>

        <section data-testid="support-links">
          <h2 className="font-display text-xl mt-6 mb-2 text-[var(--jc-text)]">
            More
          </h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <Link
                to="/login"
                className="underline hover:text-[var(--jc-text)]"
                data-testid="support-link-login"
              >
                Sign in to JobClose AI
              </Link>
            </li>
            <li>
              <Link
                to="/privacy"
                className="underline hover:text-[var(--jc-text)]"
                data-testid="support-link-privacy"
              >
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link
                to="/terms"
                className="underline hover:text-[var(--jc-text)]"
                data-testid="support-link-terms"
              >
                Terms of Use
              </Link>
            </li>
            <li>
              <Link
                to="/data-security"
                className="underline hover:text-[var(--jc-text)]"
                data-testid="support-link-data-security"
              >
                Data Security
              </Link>
            </li>
          </ul>
        </section>
      </div>
    </main>

    <footer className="border-t border-[var(--jc-border)] py-10">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-[var(--jc-text-soft)]">
        <div>
          © {new Date().getFullYear()} JobClose AI · Built by a contractor,
          for contractors.
        </div>
        <div className="flex items-center gap-5">
          <Link to="/privacy" className="hover:text-[var(--jc-text)]">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-[var(--jc-text)]">
            Terms
          </Link>
          <Link to="/data-security" className="hover:text-[var(--jc-text)]">
            Data Security
          </Link>
        </div>
      </div>
    </footer>
  </div>
);

export default Support;
